'use client';

import {
	BigNum,
	EventMap,
	MarketType,
	OrderAction,
	OrderActionExplanation,
	PositionDirection,
	PRICE_PRECISION_EXP,
	PublicKey,
	WrappedEvent,
	ZERO,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	MarketId,
	Serializer,
	getVelocityEventKey,
	UniqableVelocityEvent,
	dedupeSubject,
} from '@velocity-exchange/common';
import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { dlog } from '../../dev';
import useAppEventEmitter from '../../hooks/useAppEventEmitter';
import useCurrentUserAccount from '../../hooks/useCurrentUserAccount';
import { VelocityBlockchainEventSubjectContext } from '../../providers/velocityEvents/velocityEventSubjectProvider';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import {
	MarketOrderToastOrderInfo,
	MarketOrderToastStateTransitionEvent,
} from './MarketOrderToastStateTypes';
import useCurrentSlotRef from '../../hooks/useCurrentSlotRef';
import { getChainClockTs } from '../../hooks/useChainClockTs';
import useInterval from '../../hooks/useInterval';
import { MarketOrderToastId } from './MarketOrderToastStateTypes';
import {
	registerMarketOrderToastOrderId,
	unregisterMarketOrderToastOrderId,
} from './marketOrderToastTracking';
import { SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY_OFFSET } from 'src/constants/constants';
import { getMarketStepSize } from '../../hooks/useMarketStepSize';
import useMarketsInfoStore from '../../stores/useMarketsInfoStore';
import Env from 'src/environmentVariables/EnvironmentVariables';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import {
	OrderId,
	UserOrderId,
	V2MarketOrderToastIdHandler,
} from './MarketOrderToastIdHandler';
import {
	createInitialOrderInfoFromEvent,
	isRelevantMarketOrder,
} from './marketOrderToastUtils';
import VelocityAppEvents from 'src/utils/VelocityAppEvents';
import { LEDGER_SIGN_OFF_CHAIN_MESSAGE_ERROR_MESSAGE } from 'src/utils/TransactionErrorHandler/consts';
import { notify } from 'src/utils/notifications';
import { OrderFillTimeTracker } from '../../utils/orderFillTimeTracker';
import useNotificationStore from 'src/stores/useNotificationStore';

const getShouldUseBlockHeightOffset = (): boolean => {
	const defaultValue = true;

	const rpcVersion = Env.rpcVersion;

	const solanaCoreVersion = rpcVersion?.['solana-core'];

	if (!solanaCoreVersion) return defaultValue;

	const majorVersion = solanaCoreVersion.split('.')[0];

	if (!majorVersion) return defaultValue;

	const parsedMajorVersion = parseInt(majorVersion);

	if (isNaN(parsedMajorVersion)) return defaultValue;

	if (parsedMajorVersion >= 2) {
		return false;
	}

	return defaultValue;
};

/**
 * This provider is in charge of emitting events for the MarketOrderToast component. It listens to events from on chain, and other sources, to determine what events to emit for our context. These events should always flow through here so that there's a single source of truth for state changes.
 */

const getIsTaker = (
	event: WrappedEvent<'OrderActionRecord'>,
	user: PublicKey
) => {
	const isTaker = event?.taker?.equals(user);
	const isMaker = event?.maker?.equals(user);

	if (!isTaker && !isMaker) {
		throw new Error(
			`Got user order action event where was not matching taker or maker`
		);
	}

	return isTaker;
};

const getToastIdFromOrderActionRecord = (
	event: WrappedEvent<'OrderActionRecord'>,
	user: PublicKey
): MarketOrderToastId | null => {
	const marketId = new MarketId(event.marketIndex, event.marketType);

	const isTaker = getIsTaker(event, user);

	const orderId = isTaker ? event.takerOrderId : event.makerOrderId;

	return V2MarketOrderToastIdHandler.handleActionEvent({
		user,
		marketId,
		orderId: orderId as OrderId,
		actionRecord: event,
	});
};

const getToastIdFromOrderRecord = (
	event: WrappedEvent<'OrderRecord'>,
	user: PublicKey
) => {
	const marketId = ENUM_UTILS.match(event.order.marketType, MarketType.PERP)
		? MarketId.createPerpMarket(event.order.marketIndex)
		: MarketId.createSpotMarket(event.order.marketIndex);

	return V2MarketOrderToastIdHandler.handleOrderEvent({
		user,
		marketId,
		orderId: event.order.orderId as OrderId,
		userOrderId: event.order.userOrderId as UserOrderId,
	});
};

type MarketOrderToastEvent = MarketOrderToastStateTransitionEvent;

type MarketOrderToastEventSubject = {
	subject: Subject<MarketOrderToastEvent>;
};

const INITIAL_CONTEXT_VALUE: MarketOrderToastEventSubject = {
	subject: new Subject<MarketOrderToastEvent>(),
};

export const MarketOrderToastEventContext =
	React.createContext<MarketOrderToastEventSubject>(INITIAL_CONTEXT_VALUE);

export const useCurrentUserForMarketOrderEventsRef = () => {
	const currentUserRef = useRef<PublicKey>(null);
	const currentUser = useCurrentUserAccount();

	useEffect(() => {
		currentUserRef.current = currentUser?.pubKey ?? null;
	}, [currentUser?.pubKey]);

	return currentUserRef;
};

const useUserEventsObservable = () => {
	const velocityEventSubjectContext = useContext(
		VelocityBlockchainEventSubjectContext
	);
	return velocityEventSubjectContext.userEventObservable;
};

const inputToastVelocityEventsSubject = new Subject<
	WrappedEvent<keyof EventMap>
>(); // We can pipe drift events from multiple sources into this subject regardless of being duplicate events or not and they will be handled appropriately.
const outputToastVelocityEventsSubject = dedupeSubject(
	inputToastVelocityEventsSubject,
	100, // Buffer size for deduping :: this will store the most recent 100 unique events (duplicates do not take up space)
	(event) => {
		try {
			return getVelocityEventKey(event as UniqableVelocityEvent);
		} catch {
			return `unhandled_${event.eventType}`;
		}
	}
); // This subject will act as the source of truth for all drift events that need to be handled by the toast logic. Does processing on the input subject to ensure that we only handle each event once, etc.

export type EventHandlingProps = {
	onParsedEvent: (event: MarketOrderToastStateTransitionEvent) => void;
	knownOrders: React.MutableRefObject<
		Map<MarketOrderToastId, MarketOrderToastOrderInfo>
	>;
	setOrUpdateKnownOrder: (
		orderId: MarketOrderToastId,
		newData: Partial<MarketOrderToastOrderInfo>
	) => void;
	checkOrderCompleteAfterUpdate: (orderId: MarketOrderToastId) => boolean;
	clearKnownOrder: (orderId: MarketOrderToastId) => void;
};

/**
 * This hook pipes the blockchain events through to the toast events subject to be deduped
 */
const usePipeBlockchainEventsToInput = () => {
	const userEventsObservable = useUserEventsObservable();
	const currentUserRef = useCurrentUserForMarketOrderEventsRef();

	const pipeEventToSubject = (event: WrappedEvent<keyof EventMap>) => {
		inputToastVelocityEventsSubject.next(event);
	};

	const blockchainEventSyncingDisabled = useFeatureFlagStore((s) =>
		s.flagEnabled('DISABLE_BLOCKCHAIN_EVENTS_FOR_MARKET_ORDER_TOASTS')
	);

	useEffect(() => {
		if (blockchainEventSyncingDisabled) return;
		if (!userEventsObservable) return;

		const subscription = userEventsObservable.subscribe((event) => {
			if (!currentUserRef.current) {
				console.warn(
					`Got user event when there was no user account in MarketOrderToastEventProvider.`
				);
				return;
			}

			pipeEventToSubject(event);
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [userEventsObservable, blockchainEventSyncingDisabled]);
};

const useHandleOutputToastVelocityEvents = (props: EventHandlingProps) => {
	const eventsObservable = outputToastVelocityEventsSubject;
	const currentUserRef = useCurrentUserForMarketOrderEventsRef();

	const knownOrders = props.knownOrders;

	const handleOrderActionRecord = (
		event: WrappedEvent<'OrderActionRecord'>
	) => {
		if (!currentUserRef.current) return;

		const actionTypeStr = ENUM_UTILS.toStr(event.action);

		const actionExplanationStr = ENUM_UTILS.toStr(event.actionExplanation);

		dlog(
			`v2_auctions`,
			`order_action_record::${actionTypeStr}`,
			`order_action_explanation::${actionExplanationStr}`
		);

		const orderAction = event.action;

		const isTaker = event?.taker?.equals(currentUserRef.current);
		const isMaker = event?.maker?.equals(currentUserRef.current);

		if (!isTaker && !isMaker) {
			return;
		}

		const toastId = getToastIdFromOrderActionRecord(
			event,
			currentUserRef.current
		);

		if (!toastId) {
			dlog('v2_auctions', 'order_action_record_no_matching_toast');
			// Bracket orders can create events which we won't have a toast ID to link to, so we're happy to ignore a non-matching toast ID ... this _is_ a problem if it happens outside of bracket orders though
			return;
		}

		const baseAmountOrdered = isTaker
			? event.takerOrderBaseAssetAmount
			: event.makerOrderBaseAssetAmount;

		const cumulativeBaseFilled = isTaker
			? event.takerOrderCumulativeBaseAssetAmountFilled
			: event.makerOrderCumulativeBaseAssetAmountFilled;

		const cumulativeQuoteFilled = isTaker
			? event.takerOrderCumulativeQuoteAssetAmountFilled
			: event.makerOrderCumulativeQuoteAssetAmountFilled;

		const prevKnownOrder = knownOrders.current.get(toastId);

		const orderCompletelyFilled = !!(
			prevKnownOrder?.baseAmountOrdered &&
			cumulativeBaseFilled?.eq(prevKnownOrder?.baseAmountOrdered)
		);

		const orderEnded =
			ENUM_UTILS.match(orderAction, OrderAction.CANCEL) ||
			ENUM_UTILS.match(orderAction, OrderAction.EXPIRE);

		const updateData: Partial<MarketOrderToastOrderInfo> = {
			marketId: new MarketId(event.marketIndex, event.marketType),
			orderId: isTaker
				? (event.takerOrderId as number)
				: (event.makerOrderId as number),
			toastId: toastId,
			baseAmountOrdered: baseAmountOrdered ?? undefined,
			lastAction: event.action,
			lastActionExplanation: event.actionExplanation,
			direction: isTaker
				? (event.takerOrderDirection as PositionDirection)
				: (event.makerOrderDirection as PositionDirection),
			auctionEnded:
				prevKnownOrder?.auctionEnded || orderCompletelyFilled || orderEnded,
		};

		props.setOrUpdateKnownOrder(toastId, updateData);

		// Get new order state after update
		const newOrderState = knownOrders.current.get(toastId);

		if (!newOrderState) {
			return;
		}

		if (ENUM_UTILS.match(orderAction, OrderAction.CANCEL)) {
			if (
				ENUM_UTILS.match(
					event.actionExplanation,
					OrderActionExplanation.ORDER_EXPIRED
				)
			) {
				props.onParsedEvent({
					toastId: toastId,
					type: 'action_expired',
					data: newOrderState,
				});
			} else {
				props.onParsedEvent({
					toastId: toastId,
					type: 'action_cancelled',
					data: newOrderState,
				});
			}
			return;
		}

		if (ENUM_UTILS.match(orderAction, OrderAction.EXPIRE)) {
			props.onParsedEvent({
				toastId: toastId,
				type: 'action_expired',
				data: newOrderState,
			});
			return;
		}

		if (ENUM_UTILS.match(orderAction, OrderAction.FILL)) {
			if (orderCompletelyFilled) {
				props.setOrUpdateKnownOrder(toastId, {
					cumulativeBaseAmountFilled: cumulativeBaseFilled ?? undefined,
					cumulativeQuoteAmountFilled: cumulativeQuoteFilled ?? undefined,
				});

				// pull from map again after mutating state
				const updatedOrderState = knownOrders.current.get(toastId);

				// Is a completely filled order
				props.onParsedEvent({
					toastId: toastId,
					type: 'order_completely_filled',
					data: {
						...updatedOrderState,
						auctionEnded: true,
					},
				});
			} else {
				// Is a partially filled order
				const existingSource =
					knownOrders.current.get(toastId)?.partialFillEventSource;
				if (!existingSource) {
					props.setOrUpdateKnownOrder(toastId, {
						partialFillEventSource: 'orderAction',
					});
				}
				const currentSource =
					knownOrders.current.get(toastId)?.partialFillEventSource;
				if (currentSource && currentSource !== 'orderAction') {
					return;
				}
				props.setOrUpdateKnownOrder(toastId, {
					cumulativeBaseAmountFilled: cumulativeBaseFilled ?? undefined,
					cumulativeQuoteAmountFilled: cumulativeQuoteFilled ?? undefined,
				});
				props.onParsedEvent({
					toastId: toastId,
					type: 'receive_fill_action',
					data: newOrderState,
				});
			}
		}

		if (ENUM_UTILS.match(orderAction, OrderAction.PLACE)) {
			if (prevKnownOrder && !prevKnownOrder.auctionEnabled) {
				props.onParsedEvent({
					toastId: toastId,
					type: 'received_confirmation_and_auction_disabled',
					data: newOrderState,
				});
			} else {
				props.onParsedEvent({
					toastId: toastId,
					type:
						prevKnownOrder?.orderRoute === 'signedMsg'
							? 'receive_confirmation_signed_msg_order'
							: 'receive_confirmation',
					data: newOrderState,
				});
			}
		}
	};

	const handleOrderRecord = (event: WrappedEvent<'OrderRecord'>) => {
		dlog('v2_auctions', 'handleOrderRecord', event);

		const serializedOrder = Serializer.Serialize.Order(event.order);
		if (!serializedOrder) return;
		const uiOrder = Serializer.Deserialize.UIOrder(serializedOrder);
		if (!isRelevantMarketOrder(uiOrder)) {
			dlog(
				`v2_auctions`,
				`skipping_order_record::${ENUM_UTILS.toStr(event.order.orderType)}`
			);
			return;
		}

		const marketId = new MarketId(
			event.order.marketIndex,
			event.order.marketType
		);

		// const toastId = getToastIdFromEvent(event, currentUserRef.current);

		if (!currentUserRef.current) return;
		if (!event.user.equals(currentUserRef.current)) return;

		const { toastId, unhandledActionRecords } = getToastIdFromOrderRecord(
			event,
			currentUserRef.current
		);

		if (!toastId) {
			// Bracket orders can create events which we won't have a toast ID to link to, so we're happy to ignore a non-matching toast ID ... this _is_ a problem if it happens outside of bracket orders though
			return;
		}

		const knownOrder = knownOrders.current.get(toastId);

		if (!knownOrder) {
			return;
		}

		props.setOrUpdateKnownOrder(toastId, {
			marketId,
			toastId: toastId,
			orderId: event.order.orderId,
			baseAmountOrdered: event.order.baseAssetAmount,
			cumulativeBaseAmountFilled:
				knownOrder?.cumulativeBaseAmountFilled ?? ZERO,
			cumulativeQuoteAmountFilled:
				knownOrder?.cumulativeQuoteAmountFilled ?? ZERO,
			direction: event.order.direction,
			auctionStartSlot: event.order.slot.toNumber(),
			auctionDuration: event.order.auctionDuration,
			maxTs: event.order.maxTs,
			auctionMaxPrice: BigNum.from(
				event.order.auctionEndPrice,
				PRICE_PRECISION_EXP
			).prettyPrint(true),
		});

		const knownOrderUpdated = knownOrders.current.get(toastId);
		dlog(
			'v2_auctions',
			'handleOrderRecord',
			'knownOrderUpdated',
			knownOrderUpdated,
			'cumulative base amount Filled',
			knownOrderUpdated?.cumulativeBaseAmountFilled.toString()
		);

		dlog('v2_auctions', 'handleOrderRecord', 'knownOrders', knownOrders);

		if (!knownOrder.auctionEnabled) {
			props.onParsedEvent({
				toastId: toastId,
				type: 'received_confirmation_and_auction_disabled',
				data: knownOrderUpdated ?? knownOrder,
			});
			return;
		}

		// Check if the order is already finished .. this can happen if we receive the order actions for the filled order before we receive the order record
		const orderIsFinished = props.checkOrderCompleteAfterUpdate(toastId);

		if (orderIsFinished) {
			const finishedKnownOrder = knownOrders.current.get(toastId);

			props.onParsedEvent({
				toastId: toastId,
				type: 'order_completely_filled',
				data: {
					cumulativeBaseAmountFilled:
						finishedKnownOrder?.cumulativeBaseAmountFilled,
					cumulativeQuoteAmountFilled:
						finishedKnownOrder?.cumulativeQuoteAmountFilled,
					...finishedKnownOrder,
				},
			});
		} else {
			props.onParsedEvent({
				toastId: toastId,
				type: 'receive_order_record',
				data: knownOrderUpdated ?? knownOrder,
			});

			if (unhandledActionRecords.length > 0) {
				dlog(
					`v2_auctions`,
					`found_unhandled_action_records_when_handling_order_record::${toastId}`
				);

				unhandledActionRecords.forEach((actionRecord) => {
					inputToastVelocityEventsSubject.next(actionRecord);
				});
			}
		}
	};

	useEffect(() => {
		if (!eventsObservable) return;

		const subscription = eventsObservable.subscribe((event) => {
			if (!currentUserRef.current) {
				console.warn(
					`Got user event when there was no user account in MarketOrderToastEventProvider.`
				);
				return;
			}

			switch (event.eventType) {
				case 'OrderActionRecord': {
					const typedEvent = event as WrappedEvent<'OrderActionRecord'>;
					handleOrderActionRecord(typedEvent);
					break;
				}
				case 'OrderRecord': {
					const typedEvent = event as WrappedEvent<'OrderRecord'>;
					handleOrderRecord(typedEvent);
					break;
				}
				default: {
					// Do nothing - These events don't matter to us
				}
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [eventsObservable]);
};

const useHandleAppEvents = (hookProps: EventHandlingProps) => {
	const appEventEmitter = useAppEventEmitter();
	const currentUserRef = useCurrentUserForMarketOrderEventsRef();
	const currentSlotRef = useCurrentSlotRef(1000);
	const connection = useVelocityStore((s) => s.connection.current);
	const marketInfoStoreGetter = useMarketsInfoStore((s) => s.get);
	const { captureEvent } = usePostHogCapture();
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const { removeNotificationById } = useNotificationStore();

	// # Handle expect incoming auction
	useEffect(() => {
		const handler: VelocityAppEvents['expectedAuctionInit'] = (
			handlerProps
		) => {
			const marketId = handlerProps.v2Props.marketId;

			if (handlerProps.v2Props.userOrderId == undefined) {
				throw new Error(
					`Received expectedAuctionInit event without a userOrderId`
				);
			}

			const timestamp = Date.now();
			const toastId = V2MarketOrderToastIdHandler.handleInitEvent({
				user: currentUserRef.current as PublicKey,
				marketId,
				userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
				timestamp,
			});

			const initEvent: MarketOrderToastStateTransitionEvent = {
				type: 'init',
				toastId,
				data: {
					marketId: handlerProps.v2Props.marketId,
					userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
					cumulativeBaseAmountFilled: ZERO,
					cumulativeQuoteAmountFilled: ZERO,
					baseAmountOrdered: handlerProps.v2Props.baseAmountOrdered,
					direction: handlerProps.v2Props.direction,
					auctionEnabled: handlerProps.v2Props.auctionEnabled,
					includesSlOrder: handlerProps.v2Props.includesSlOrder,
					includesTpOrder: handlerProps.v2Props.includesTpOrder,
					deferUntilFill: handlerProps.deferUntilFill,
				},
			};

			const orderInfo = createInitialOrderInfoFromEvent(initEvent, toastId, {
				stepSize: getMarketStepSize(
					marketId,
					marketInfoStoreGetter,
					spotMarketsSortedByMarketIndex
				),
				toastInitBlockHeight: currentSlotRef.current,
				isAutoSigned: handlerProps.isAutoSigned,
			});

			const orderProps: Partial<MarketOrderToastOrderInfo> = orderInfo;

			hookProps.setOrUpdateKnownOrder(toastId, orderProps);

			hookProps.onParsedEvent({
				type: 'init',
				toastId: toastId,
				data: orderProps,
			});
		};

		appEventEmitter.on('expectedAuctionInit', handler);

		return () => {
			appEventEmitter.off('expectedAuctionInit', handler);
		};
	}, [appEventEmitter]);

	// # Handle cancel expected auction
	useEffect(() => {
		const handler: VelocityAppEvents['expectedAuctionTxFailed'] = (
			handlerProps
		) => {
			let marketOrderId: MarketOrderToastId;

			try {
				marketOrderId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.v2Props.marketId,
					userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
				}) as MarketOrderToastId;
			} catch {
				// It's ok if we can't find the toast id when handling a failure event
				return;
			}

			const knownOrder = hookProps.knownOrders.current.get(marketOrderId);

			if (!knownOrder) {
				return;
			}

			hookProps.onParsedEvent({
				toastId: marketOrderId,
				type: 'tx_failed',
				data: knownOrder,
			});

			hookProps.clearKnownOrder(marketOrderId);
		};

		appEventEmitter.on('expectedAuctionTxFailed', handler);

		return () => {
			appEventEmitter.off('expectedAuctionTxFailed', handler);
		};
	}, [appEventEmitter]);

	// # Handle aborted expected auction: remove the toast instead of showing a failure
	useEffect(() => {
		const handler: VelocityAppEvents['expectedAuctionTxAborted'] = (
			handlerProps
		) => {
			let marketOrderId: MarketOrderToastId | null;

			try {
				marketOrderId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.v2Props.marketId,
					userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
				}) as MarketOrderToastId | null;
			} catch {
				// Nothing to remove if the toast id cannot be resolved
				return;
			}

			if (!marketOrderId) {
				return;
			}

			hookProps.onParsedEvent({
				toastId: marketOrderId,
				type: 'aborted',
				data: {},
			});

			hookProps.clearKnownOrder(marketOrderId);
		};

		appEventEmitter.on('expectedAuctionTxAborted', handler);

		return () => {
			appEventEmitter.off('expectedAuctionTxAborted', handler);
		};
	}, [appEventEmitter]);

	// # Handle market order confirmed
	useEffect(() => {
		const handler: VelocityAppEvents['expectedAuctionTxConfirmed'] = (
			handlerProps
		) => {
			const marketOrderId =
				V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.v2Props.marketId,
					userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
				}) as MarketOrderToastId;

			const knownOrder = hookProps.knownOrders.current.get(marketOrderId);

			if (!knownOrder) {
				return;
			}

			hookProps.onParsedEvent({
				toastId: marketOrderId,
				type: knownOrder.auctionEnabled
					? 'receive_confirmation'
					: 'received_confirmation_and_auction_disabled',
				data: knownOrder,
			});

			// Track order confirmation time if we have an order id
			if (knownOrder.orderId != null) {
				try {
					OrderFillTimeTracker.trackOrderConfirmation(
						knownOrder.orderId as number,
						Date.now(),
						undefined,
						currentUserRef.current?.toBase58()
					);
				} catch {
					// ignore
				}
			}
		};

		appEventEmitter.on('expectedAuctionTxConfirmed', handler);

		return () => {
			appEventEmitter.off('expectedAuctionTxConfirmed', handler);
		};
	}, [appEventEmitter]);

	// # Handle Tx Signed
	useEffect(() => {
		const handler: VelocityAppEvents['expectedAuctionTxSigned'] = async (
			handlerProps
		) => {
			const toastId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
				user: currentUserRef.current as PublicKey,
				marketId: handlerProps.v2Props.marketId,
				userOrderId: handlerProps.v2Props.userOrderId as UserOrderId,
			}) as MarketOrderToastId;

			const knownOrder = hookProps.knownOrders.current.get(toastId);

			if (!knownOrder) {
				const errorMessage = `Could not find known order for toast id ${toastId} when handling tx signed event`;

				captureEvent('trade_market_order_error', {
					message: errorMessage,
				});

				throw new Error(errorMessage);
			}

			let signedTxLastValidBlockHeight =
				handlerProps?.signedTxData?.[0]?.lastValidBlockHeight;

			if (!signedTxLastValidBlockHeight) {
				const errorMessage = `Received expectedAuctionTxSigned event without a lastValidBlockHeight in the signedTxData`;

				dlog(
					`v2_auctions`,
					`no_last_valid_block_height_on_tx_signed => falling back to current block height`
				);

				captureEvent('trade_market_order_error', {
					message: errorMessage,
				});

				const currentBlockHeightResult =
					await connection?.getLatestBlockhashAndContext();
				signedTxLastValidBlockHeight =
					currentBlockHeightResult?.value.lastValidBlockHeight;
			}

			const useOffset = getShouldUseBlockHeightOffset();

			const txExpiryBlockHeight =
				(signedTxLastValidBlockHeight ?? 0) +
				(useOffset ? SOLANA_TRANSACTION_BLOCK_HEIGHT_EXPIRY_OFFSET : 0);

			dlog(
				`v2_auctions`,
				`set_expiry_slot_with_tx_signature .. ${txExpiryBlockHeight}`
			);

			hookProps.setOrUpdateKnownOrder(toastId as MarketOrderToastId, {
				signed: true,
			});

			const newOrderStateAfterUpdate = hookProps.knownOrders.current.get(
				toastId as MarketOrderToastId
			);

			hookProps.onParsedEvent({
				toastId: toastId as MarketOrderToastId,
				type: 'tx_signed',
				data: newOrderStateAfterUpdate as Partial<MarketOrderToastOrderInfo>,
			});
		};

		appEventEmitter.on('expectedAuctionTxSigned', handler);

		return () => {
			appEventEmitter.off('expectedAuctionTxSigned', handler);
		};
	}, [appEventEmitter, connection]);

	// # Handle signed message order initialized
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderInit'] = (handlerProps) => {
			const marketId = handlerProps.orderProps.marketId;

			const timestamp = Date.now();
			const toastId = V2MarketOrderToastIdHandler.handleInitEvent({
				user: currentUserRef.current as PublicKey,
				marketId,
				userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
				timestamp,
			});

			const initEvent: MarketOrderToastStateTransitionEvent = {
				type: 'signed_msg_order_init',
				toastId,
				data: {
					marketId: handlerProps.orderProps.marketId,
					userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
					cumulativeBaseAmountFilled: ZERO,
					cumulativeQuoteAmountFilled: ZERO,
					baseAmountOrdered: handlerProps.orderProps.baseAmountOrdered,
					direction: handlerProps.orderProps.direction,
					auctionEnabled:
						(handlerProps.signedMsgOrderPayload?.signedMsgOrderParams
							?.auctionDuration ?? 0) > 0,
					includesSlOrder: handlerProps.orderProps.includesSlOrder,
					includesTpOrder: handlerProps.orderProps.includesTpOrder,
					orderRoute: 'signedMsg',
					originalTradeFormDetails: handlerProps.originalTradeFormDetails,
				},
			};

			const orderInfo = createInitialOrderInfoFromEvent(initEvent, toastId, {
				auctionStartSlot: handlerProps.signedMsgOrderPayload?.slot.toNumber(),
				auctionDuration: handlerProps.signedMsgOrderPayload
					?.signedMsgOrderParams.auctionDuration as number,
				stepSize: getMarketStepSize(
					marketId,
					marketInfoStoreGetter,
					spotMarketsSortedByMarketIndex
				),
				toastInitBlockHeight: currentSlotRef.current,
				isAutoSigned: handlerProps.isAutoSigned,
			});

			const orderProps: Partial<MarketOrderToastOrderInfo> = orderInfo;

			hookProps.setOrUpdateKnownOrder(toastId, orderProps);

			hookProps.onParsedEvent({
				type: 'signed_msg_order_init',
				toastId: toastId,
				data: orderProps,
			});
		};

		appEventEmitter.on('signedMsgOrderInit', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderInit', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order not signed in time
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderMessageSlotExpired'] = (
			handlerProps
		) => {
			const marketOrderId =
				V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.orderProps.marketId,
					userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
				});

			const knownOrder = hookProps.knownOrders.current.get(
				marketOrderId as MarketOrderToastId
			);

			if (knownOrder?.signed) {
				return;
			}

			const orderProps: Partial<MarketOrderToastOrderInfo> = {
				marketId: handlerProps.orderProps.marketId,
				toastId: marketOrderId as MarketOrderToastId,
				cumulativeBaseAmountFilled: ZERO,
				cumulativeQuoteAmountFilled: ZERO,
				baseAmountOrdered: handlerProps.orderProps.baseAmountOrdered,
				direction: handlerProps.orderProps.direction,
				auctionEnabled:
					(handlerProps.signedMsgOrderPayload?.signedMsgOrderParams
						?.auctionDuration ?? 0) > 0,
				lastAction: undefined,
				lastActionExplanation: undefined,
				auctionStartSlot: handlerProps.signedMsgOrderPayload?.slot.toNumber(),
				auctionDuration: handlerProps.signedMsgOrderPayload
					?.signedMsgOrderParams.auctionDuration as number,
				maxTs: undefined,
				includesSlOrder: handlerProps.orderProps.includesSlOrder,
				includesTpOrder: handlerProps.orderProps.includesTpOrder,
				stepSize: getMarketStepSize(
					handlerProps.orderProps.marketId,
					marketInfoStoreGetter,
					spotMarketsSortedByMarketIndex
				),
				originalTradeFormDetails: handlerProps.originalTradeFormDetails,
			};

			hookProps.onParsedEvent({
				type: 'signed_msg_order_message_slot_expired',
				toastId: marketOrderId as MarketOrderToastId,
				data: orderProps,
			});
		};

		appEventEmitter.on('signedMsgOrderMessageSlotExpired', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderMessageSlotExpired', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order signed
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderSigned'] = (
			handlerProps
		) => {
			const toastId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
				user: currentUserRef.current as PublicKey,
				marketId: handlerProps.orderProps.marketId,
				userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
			}) as MarketOrderToastId;

			const knownOrder = hookProps.knownOrders.current.get(
				toastId as MarketOrderToastId
			);

			if (!knownOrder) {
				const errorMessage = `Could not find known order for toast id ${toastId} when handling tx signed event`;

				captureEvent('trade_market_order_error', {
					message: errorMessage,
				});

				throw new Error(errorMessage);
			}

			const orderProps: Partial<MarketOrderToastOrderInfo> = {
				marketId: handlerProps.orderProps.marketId,
				toastId: toastId,
				cumulativeBaseAmountFilled: ZERO,
				cumulativeQuoteAmountFilled: ZERO,
				baseAmountOrdered: handlerProps.orderProps.baseAmountOrdered,
				direction: handlerProps.orderProps.direction,
				auctionEnabled:
					(handlerProps.signedMsgOrderPayload?.signedMsgOrderParams
						?.auctionDuration ?? 0) > 0,
				lastAction: undefined,
				lastActionExplanation: undefined,
				auctionStartSlot: handlerProps.signedMsgOrderPayload?.slot.toNumber(),
				auctionDuration: handlerProps.signedMsgOrderPayload
					?.signedMsgOrderParams.auctionDuration as number,
				maxTs: undefined,
				includesSlOrder: handlerProps.orderProps.includesSlOrder,
				includesTpOrder: handlerProps.orderProps.includesTpOrder,
				stepSize: getMarketStepSize(
					handlerProps.orderProps.marketId,
					marketInfoStoreGetter,
					spotMarketsSortedByMarketIndex
				),
				signed: true,
				originalTradeFormDetails: handlerProps.originalTradeFormDetails,
			};

			hookProps.setOrUpdateKnownOrder(toastId, orderProps);

			hookProps.onParsedEvent({
				toastId: toastId,
				type: 'tx_signed',
				data: orderProps,
			});
		};

		appEventEmitter.on('signedMsgOrderSigned', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderSigned', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order expired
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderExpired'] = (
			handlerProps
		) => {
			const toastId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
				user: currentUserRef.current as PublicKey,
				marketId: handlerProps.orderProps.marketId,
				userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
			});

			const orderProps: Partial<MarketOrderToastOrderInfo> = {
				marketId: handlerProps.orderProps.marketId,
				toastId: toastId as MarketOrderToastId,
				cumulativeBaseAmountFilled: ZERO,
				cumulativeQuoteAmountFilled: ZERO,
				baseAmountOrdered: handlerProps.orderProps.baseAmountOrdered,
				direction: handlerProps.orderProps.direction,
				auctionEnabled:
					(handlerProps.signedMsgOrderPayload?.signedMsgOrderParams
						?.auctionDuration ?? 0) > 0,
				lastAction: undefined,
				lastActionExplanation: undefined,
				auctionStartSlot: handlerProps.signedMsgOrderPayload?.slot.toNumber(),
				auctionDuration: handlerProps.signedMsgOrderPayload
					?.signedMsgOrderParams.auctionDuration as number,
				maxTs: undefined,
				includesSlOrder: handlerProps.orderProps.includesSlOrder,
				includesTpOrder: handlerProps.orderProps.includesTpOrder,
				stepSize: getMarketStepSize(
					handlerProps.orderProps.marketId,
					marketInfoStoreGetter,
					spotMarketsSortedByMarketIndex
				),
				originalTradeFormDetails: handlerProps.originalTradeFormDetails,
			};

			hookProps.setOrUpdateKnownOrder(
				toastId as MarketOrderToastId,
				orderProps
			);

			hookProps.onParsedEvent({
				type: 'signed_msg_order_expired',
				toastId: toastId as MarketOrderToastId,
				data: orderProps,
			});
		};

		appEventEmitter.on('signedMsgOrderExpired', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderExpired', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order confirmed
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderConfirmed'] = (
			handlerProps
		) => {
			dlog(
				'v2_auctions',
				'received_signedMsgOrderConfirmed_event',
				handlerProps
			);

			const toastId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
				user: currentUserRef.current as PublicKey,
				marketId: handlerProps.orderProps.marketId,
				userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
			});

			const order = hookProps.knownOrders.current.get(
				toastId as MarketOrderToastId
			);

			if (!order) {
				dlog(
					'v2_auctions',
					'received_signedMsgOrderConfirmed_no_order_found',
					handlerProps
				);
				return;
			}

			if (order.orderId == undefined) {
				dlog(
					'v2_auctions',
					'received_signedMsgOrderConfirmed_before_order_id_set',
					handlerProps
				);
			}

			// update the order with the order ID from confirmation
			if (handlerProps.orderId != undefined) {
				const orderProps: Partial<MarketOrderToastOrderInfo> = {
					...order,
					orderId: handlerProps.orderId,
				};

				dlog(
					'v2_auctions',
					'received_signedMsgOrderConfirmed_setting_order_id',
					handlerProps
				);

				hookProps.setOrUpdateKnownOrder(
					toastId as MarketOrderToastId,
					orderProps
				);
			}

			const auctionEnabled = order.auctionEnabled;

			hookProps.onParsedEvent({
				type: auctionEnabled
					? 'receive_confirmation_signed_msg_order'
					: 'received_confirmation_and_auction_disabled',
				toastId: toastId as MarketOrderToastId,
				data: order,
			});

			// If we have an order ID in this confirmation but the known order doesn't have an order ID, we should check for any unhandled action records
			if (handlerProps.orderId != undefined && order.orderId == undefined) {
				const { unhandledActionRecords } =
					V2MarketOrderToastIdHandler.handleOrderConfirmationWithOrderId({
						user: currentUserRef.current as PublicKey,
						marketId: handlerProps.orderProps.marketId,
						userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
						orderId: handlerProps.orderId as OrderId,
					});

				dlog(
					'v2_auctions',
					'piping_unhandled_action_records_in_signedMsgOrderConfirmed_handler',
					unhandledActionRecords
				);

				unhandledActionRecords.forEach((orderActionRecord) => {
					inputToastVelocityEventsSubject.next(orderActionRecord);
				});
			}

			// Track order confirmation time when order id is known
			const orderIdForTracking =
				(handlerProps.orderId as number | undefined) ??
				(order.orderId as number | undefined);
			if (orderIdForTracking != null) {
				try {
					OrderFillTimeTracker.trackOrderConfirmation(
						orderIdForTracking,
						Date.now(),
						undefined,
						currentUserRef.current?.toBase58()
					);
				} catch {
					// ignore
				}
			}
		};

		appEventEmitter.on('signedMsgOrderConfirmed', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderConfirmed', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order not confirmed
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderFailed'] = (
			handlerProps
		) => {
			let marketOrderId: MarketOrderToastId;

			try {
				marketOrderId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.orderProps.marketId,
					userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
				}) as MarketOrderToastId;
			} catch {
				// It's ok if we can't find the toast id when handling a failure event
				return;
			}

			const knownOrder = hookProps.knownOrders.current.get(marketOrderId);

			if (!knownOrder) {
				notify({
					type: 'error',
					message: 'Failed to open order.',
				});

				return;
			}

			hookProps.onParsedEvent({
				toastId: marketOrderId,
				type: 'tx_failed',
				data: {
					...knownOrder,
					failureMessage: handlerProps.message,
				},
			});

			hookProps.clearKnownOrder(marketOrderId);
		};

		appEventEmitter.on('signedMsgOrderFailed', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderFailed', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order retrying with more margin
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgOrderRetrying'] = (
			handlerProps
		) => {
			let marketOrderId: MarketOrderToastId = '' as MarketOrderToastId;
			try {
				marketOrderId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.orderProps.marketId,
					userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
				}) as MarketOrderToastId;
				hookProps.clearKnownOrder(marketOrderId);
			} catch (e) {
				// If we can't find the toast id, show a standalone notification
				console.warn(
					'Could not find toast id when handling signedMsgOrderRetrying event',
					e
				);
			}

			if (marketOrderId) {
				removeNotificationById(`market_order_toast_${marketOrderId}`);
			}
		};

		appEventEmitter.on('signedMsgOrderRetrying', handler);

		return () => {
			appEventEmitter.off('signedMsgOrderRetrying', handler);
		};
	}, [appEventEmitter]);

	// # Handle signed message order not confirmed
	useEffect(() => {
		const handler: VelocityAppEvents['signedMsgSignatureFailed'] = (
			handlerProps
		) => {
			let marketOrderId: MarketOrderToastId;

			try {
				marketOrderId = V2MarketOrderToastIdHandler.getToastIdForUpdateEvent({
					user: currentUserRef.current as PublicKey,
					marketId: handlerProps.orderProps.marketId,
					userOrderId: handlerProps.orderProps.userOrderId as UserOrderId,
				}) as MarketOrderToastId;
			} catch {
				// It's ok if we can't find the toast id when handling a failure event
				return;
			}

			const knownOrder = hookProps.knownOrders.current.get(marketOrderId);

			if (!knownOrder) {
				return;
			}

			hookProps.onParsedEvent({
				toastId: marketOrderId,
				type: 'tx_failed',
				data: {
					...knownOrder,
					failureMessage: LEDGER_SIGN_OFF_CHAIN_MESSAGE_ERROR_MESSAGE,
				},
			});

			hookProps.clearKnownOrder(marketOrderId);
		};

		appEventEmitter.on('signedMsgSignatureFailed', handler);

		return () => {
			appEventEmitter.off('signedMsgSignatureFailed', handler);
		};
	}, [appEventEmitter]);
};

/**
 * This hook provides a subject for domain-specific Market Order events. It listens to a variety of sources to determine which events to output, to increase reliability, including blockchain events, user events, and app events.
 * @param props
 * @returns
 */
export const MarketOrderToastEventEmitterProvider = (
	props: PropsWithChildren<any>
) => {
	const contextValue = INITIAL_CONTEXT_VALUE;
	const marketOrderToastEventsSubject = contextValue.subject;
	const currentSlotRef = useCurrentSlotRef();
	const appEventEmitter = useAppEventEmitter();
	const currentUserRef = useCurrentUserForMarketOrderEventsRef();

	const knownOrdersRef = useRef<
		Map<MarketOrderToastId, MarketOrderToastOrderInfo>
	>(new Map());

	const setOrUpdateKnownOrder = (
		orderId: MarketOrderToastId,
		newData: Partial<MarketOrderToastOrderInfo>
	) => {
		const existing = knownOrdersRef.current.get(orderId);

		if (existing) {
			knownOrdersRef.current.set(orderId, {
				...existing,
				...newData,
			});
		} else {
			knownOrdersRef.current.set(orderId, newData as MarketOrderToastOrderInfo);
		}

		// Let the client-side expiry detector know this on-chain order is already
		// tracked here, so it doesn't fire a duplicate "order expired" notification.
		// Scope by the owning user account — orderIds collide across subaccounts.
		registerMarketOrderToastOrderId(
			currentUserRef.current?.toString(),
			newData.orderId ?? existing?.orderId ?? undefined
		);
	};

	const clearKnownOrder = (orderId: MarketOrderToastId) => {
		const order = knownOrdersRef.current.get(orderId);

		if (!order) return; // It's OK for multiple effects to trigger clearing the same order, so we can ignore if we don't have it available

		unregisterMarketOrderToastOrderId(
			currentUserRef.current?.toString(),
			order.orderId
		);

		dlog('v2_auctions', 'clearing_order', {
			orderId,
			order,
		});

		V2MarketOrderToastIdHandler.clearStateForToast({
			user: currentUserRef.current as PublicKey,
			marketId: order.marketId,
			orderId: order.orderId as OrderId,
			userOrderId: order.userOrderId as UserOrderId,
		});
		knownOrdersRef.current.delete(orderId);
	};

	const checkOrderCompleteAfterUpdate = (orderId: MarketOrderToastId) => {
		const order = knownOrdersRef.current.get(orderId);

		if (!order) return false;

		return (
			order.baseAmountOrdered?.eq(order.cumulativeBaseAmountFilled) ?? false
		);
	};

	// # Set up the handler to clear known orders when the toast is removed
	useEffect(() => {
		const handler = (toastId: MarketOrderToastId) => {
			clearKnownOrder(toastId);
		};

		appEventEmitter.on('marketOrderToastRemoved', handler);

		return () => {
			appEventEmitter.off('marketOrderToastRemoved', handler);
		};
	}, [appEventEmitter]);

	// #  ⭐️ The CORE logic for triggering market order events happens in the below hooks

	// Pipe events from blockchain source into the input subject
	usePipeBlockchainEventsToInput();

	// ## Handle the output Velocity Events which need to be handled by the toast logic
	useHandleOutputToastVelocityEvents({
		onParsedEvent: (event) => {
			marketOrderToastEventsSubject.next(event);
		},
		knownOrders: knownOrdersRef,
		setOrUpdateKnownOrder,
		checkOrderCompleteAfterUpdate,
		clearKnownOrder,
	});

	// ## Trigger events from event event emitter source
	useHandleAppEvents({
		onParsedEvent: (event) => {
			marketOrderToastEventsSubject.next(event);
		},
		knownOrders: knownOrdersRef,
		setOrUpdateKnownOrder,
		checkOrderCompleteAfterUpdate,
		clearKnownOrder,
	});

	// # Handle slot progress
	useInterval(() => {
		knownOrdersRef.current.forEach((order, toastId) => {
			// # Catch Auction Expired
			//// If the auction has already ended or we don't have the necessary data, don't do anything
			const canSkipCheckingAuctionExpired =
				order.orderId == undefined || // If we don't have the order ID then we haven't had the order confirmation necessary to know when the auction will start
				!order?.auctionStartSlot ||
				!order?.auctionDuration ||
				!order?.auctionEnabled ||
				!currentSlotRef.current;

			if (!canSkipCheckingAuctionExpired) {
				const startSlot = order?.auctionStartSlot;
				const duration = order?.auctionDuration;
				const currentSlot = currentSlotRef.current;

				const auctionExpirySlot = (startSlot ?? 0) + (duration ?? 0);
				const auctionIsExpired = currentSlot > auctionExpirySlot;
				const slotDifferenceFromAuction = currentSlot - auctionExpirySlot;

				setOrUpdateKnownOrder(toastId, {
					auctionEnded: auctionIsExpired || order.auctionEnded,
				});

				// Emit exactly once: only if we haven’t already marked this order as ended.
				if (
					auctionIsExpired &&
					slotDifferenceFromAuction > Env.auctionExpirySlotBuffer &&
					!order.auctionEnded
				) {
					// we would only trigger this if the auction has expired and the current slot is more than 100 slots away from the auction expiry slot. This is a fallback expiry really
					marketOrderToastEventsSubject.next({
						type: 'auction_finished',
						toastId: toastId,
						data: order,
					});
				}
			}

			// # Catch Order Expired
			const currentTs = getChainClockTs() ?? 0;

			const canSkipCheckingOrderExpired = !order?.maxTs || !currentTs;

			if (!canSkipCheckingOrderExpired) {
				const bufferSeconds = Env.orderMaxTsBufferMs / 1000;
				const orderIsExpired =
					currentTs > (order.maxTs?.toNumber() ?? 0) + bufferSeconds;
				if (orderIsExpired) {
					dlog(
						`v2_auctions`,
						`triggering_implied_expired_event`,
						toastId,
						order
					);
					// This won't happen for swift orders if they don't land on-chain - because there won't be an order record, so no maxTs to compare against
					marketOrderToastEventsSubject.next({
						type: 'action_expired',
						toastId: toastId,
						data: order,
					});
				}
			}
		});
	}, 10_000);

	return (
		<MarketOrderToastEventContext value={contextValue}>
			{props.children}
		</MarketOrderToastEventContext>
	);
};
